import { Instance } from "mobx-state-tree";
import {
  createSurveyRuleModel,
  SurveyRuleModel,
} from "../survey-rules/models/SurveyRuleModel";
import { SurveyRule } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRule";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import {
  SurveyRuleCondition,
  SurveyRuleConditionEnums,
} from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleCondition";
import {
  createSurveyRuleConditionModel,
  SurveyRuleConditionModel,
} from "../survey-rules/models/SurveyRuleConditionModel";
import { createSurveyRuleQuestionKeyTypeModel } from "../survey-rules/models/SurveyRuleQuestionKeyTypeModel";
import { createSurveyRuleRespondentKeyTypeModel } from "../survey-rules/models/SurveyRuleRespondentKeyTypeModel";
import { SurveyRuleTarget } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleTarget";
import {
  createSurveyRuleTargetModel,
  SurveyRuleTargetModel,
} from "../survey-rules/models/SurveyRuleTargetModel";
import { Code } from "@pulse/pulse-rpcs/lib/types/code";
import { RespondentColumnName } from "@pulse/pulse-rpcs/lib/types/respondentColumnName";

const mapToSurveyRuleConditionKeyTypeModel = (
  keyType: SurveyRuleConditionEnums.KeyType.KeyType,
): Instance<typeof SurveyRuleConditionModel>["keyType"] => {
  if (keyType instanceof SurveyRuleConditionEnums.KeyType.Question) {
    return createSurveyRuleQuestionKeyTypeModel({
      type: "QUESTION",
      conditionKey: keyType.conditionKey.code,
      conditionValue: keyType.conditionValue.map((value) => value.code),
    });
  }

  if (keyType instanceof SurveyRuleConditionEnums.KeyType.Respondent) {
    return createSurveyRuleRespondentKeyTypeModel({
      type: "RESPONDENT",
      conditionKey: keyType.conditionKey.name,
      conditionValue: keyType.conditionValue,
    });
  }

  return keyType satisfies never;
};

const mapToSurveyRuleConditionModel = (
  condition: SurveyRuleCondition,
): readonly [string, Instance<typeof SurveyRuleConditionModel>] => {
  const conditionId = condition.conditionId.uuid;
  return [
    conditionId,
    createSurveyRuleConditionModel({
      conditionId,
      keyType: mapToSurveyRuleConditionKeyTypeModel(condition.keyType),
      connecter: condition.connector,
    }),
  ];
};

export const mapToSurveyRuleTargetModel = (
  target: SurveyRuleTarget,
): Instance<typeof SurveyRuleTargetModel> =>
  createSurveyRuleTargetModel({
    questionCode: target.questionCode.code,
    gridRowCode: target.gridRowCode?.code ?? null,
  });

export const mapToSurveyRuleModel = (
  surveyRule: SurveyRule,
): readonly [string, Instance<typeof SurveyRuleModel>] => {
  const ruleId = surveyRule.ruleId.uuid;
  const conditionEntries = surveyRule.conditions.map(
    mapToSurveyRuleConditionModel,
  );
  const conditionOrder = conditionEntries.map(([conditionId]) => conditionId);

  return [
    ruleId,
    createSurveyRuleModel({
      ruleId,
      conditions: Object.fromEntries(conditionEntries),
      conditionOrder,
      action: surveyRule.action,
      target: surveyRule.target.map(mapToSurveyRuleTargetModel),
    }),
  ];
};

const mapToSurveyRuleConditionKeyTypeRPCObject = (
  keyType: Instance<typeof SurveyRuleConditionModel>["keyType"],
): SurveyRuleConditionEnums.KeyType.KeyType => {
  if (!keyType.conditionKey) {
    throw new Error(
      "Survey rule key cannot be null while mapping to RPC object",
    );
  }

  switch (keyType.type) {
    case "QUESTION":
      return new SurveyRuleConditionEnums.KeyType.Question(
        new Code(keyType.conditionKey),
        keyType.conditionValue.map((value) => new Code(value)),
      );
    case "RESPONDENT":
      return new SurveyRuleConditionEnums.KeyType.Respondent(
        new RespondentColumnName(keyType.conditionKey),
        keyType.conditionValue,
      );
    default:
      return keyType satisfies never;
  }
};

const mapToSurveyRuleConditionRPCObject = (
  condition: Instance<typeof SurveyRuleConditionModel>,
): SurveyRuleCondition =>
  new SurveyRuleCondition(
    new LeoUUID(condition.conditionId),
    mapToSurveyRuleConditionKeyTypeRPCObject(condition.keyType),
    condition.connecter,
  );

const mapToSurveyRuleTargetRPCObject = (
  target: Instance<typeof SurveyRuleTargetModel>,
): SurveyRuleTarget =>
  new SurveyRuleTarget(
    new Code(target.questionCode),
    target.gridRowCode ? new Code(target.gridRowCode) : null,
  );

export const mapToSurveyRuleRPCObject = (
  surveyRule: Instance<typeof SurveyRuleModel>,
): SurveyRule => {
  if (!surveyRule.action) {
    throw new Error(
      "Survey rule action cannot be null while mapping to RPC object.",
    );
  }

  const conditions = surveyRule.conditionOrder.map((conditionId) => {
    const condition = surveyRule.getNonNullCondition(conditionId);
    return mapToSurveyRuleConditionRPCObject(condition);
  });

  return new SurveyRule(
    new LeoUUID(surveyRule.ruleId),
    conditions,
    surveyRule.action,
    surveyRule.target.map(mapToSurveyRuleTargetRPCObject),
  );
};
